import React, { useState, useEffect, useRef } from 'react';
import { 
  Phone, 
  PhoneCall, 
  PhoneOff, 
  Mic, 
  MicOff,
  Volume2,
  VolumeX
} from 'lucide-react';
import { WebRTCManager } from '../utils/webrtc';
import { VoiceCall } from '../types';

interface VoiceCallManagerProps {
  emergencyId: string;
  currentUserId: string;
  onCallStart?: (call: VoiceCall) => void;
  onCallEnd?: (callId: string) => void;
  className?: string;
}

export const VoiceCallManager: React.FC<VoiceCallManagerProps> = ({
  emergencyId,
  currentUserId,
  onCallStart,
  onCallEnd,
  className = ''
}) => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState>('new');
  
  const webrtcManager = useRef<WebRTCManager | null>(null);
  const localAudioRef = useRef<HTMLAudioElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    webrtcManager.current = new WebRTCManager();

    webrtcManager.current.onRemoteStream((stream) => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = stream;
      }
    });

    webrtcManager.current.onConnectionStateChange((state) => {
      setConnectionState(state);
      if (state === 'connected') {
        setIsConnecting(false);
        setIsCallActive(true);
      } else if (state === 'disconnected' || state === 'failed') {
        handleEndCall();
      }
    });

    return () => {
      if (webrtcManager.current) {
        webrtcManager.current.endCall();
      }
    };
  }, []);

  const handleStartCall = async () => {
    if (!webrtcManager.current) return;

    setIsConnecting(true);
    try {
      await webrtcManager.current.startCall();
      
      const localStream = webrtcManager.current.getLocalStream();
      if (localAudioRef.current && localStream) {
        localAudioRef.current.srcObject = localStream;
      }

      const newCall: VoiceCall = {
        id: Date.now().toString(),
        emergencyId,
        participants: [currentUserId],
        status: 'connecting',
        startTime: new Date()
      };

      onCallStart?.(newCall);
    } catch (error) {
      console.error('Failed to start call:', error);
      setIsConnecting(false);
      alert('Failed to start voice call. Please check your microphone permissions.');
    }
  };

  const handleEndCall = () => {
    if (webrtcManager.current) {
      webrtcManager.current.endCall();
    }
    
    setIsCallActive(false);
    setIsConnecting(false);
    setIsMuted(false);
    
    if (localAudioRef.current) {
      localAudioRef.current.srcObject = null;
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }

    onCallEnd?.('current-call');
  };

  const toggleMute = () => {
    const localStream = webrtcManager.current?.getLocalStream();
    if (localStream) {
      const audioTracks = localStream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = isMuted;
      });
      setIsMuted(!isMuted);
    }
  };

  const toggleSpeaker = () => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.muted = isSpeakerOn;
      setIsSpeakerOn(!isSpeakerOn);
    }
  };

  const getConnectionStatusColor = () => {
    switch (connectionState) {
      case 'connected':
        return 'text-green-600';
      case 'connecting':
        return 'text-yellow-600';
      case 'disconnected':
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-md p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Voice Communication</h3>
        <div className={`text-sm font-medium capitalize ${getConnectionStatusColor()}`}>
          {connectionState}
        </div>
      </div>

      {/* Audio elements */}
      <audio ref={localAudioRef} muted autoPlay />
      <audio ref={remoteAudioRef} autoPlay />

      {!isCallActive && !isConnecting ? (
        <button
          onClick={handleStartCall}
          className="w-full flex items-center justify-center gap-2 p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Phone className="w-5 h-5" />
          Start Voice Call
        </button>
      ) : (
        <div className="space-y-3">
          {isConnecting && (
            <div className="text-center text-yellow-600 font-medium">
              Connecting call...
            </div>
          )}

          {isCallActive && (
            <div className="text-center text-green-600 font-medium">
              Call Active
            </div>
          )}

          <div className="flex items-center justify-center gap-3">
            <button
              onClick={toggleMute}
              className={`p-3 rounded-full transition-colors ${
                isMuted 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>

            <button
              onClick={toggleSpeaker}
              className={`p-3 rounded-full transition-colors ${
                isSpeakerOn 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              title={isSpeakerOn ? 'Mute Speaker' : 'Unmute Speaker'}
            >
              {isSpeakerOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>

            <button
              onClick={handleEndCall}
              className="p-3 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
              title="End Call"
            >
              <PhoneOff className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};